const similarTerms = [
    "Staff Analytics Engineer",
    "Data Engineer",
    "Sports Badger"
];

export const DictionaryDefinition = () => (
    <article 
        className="w-full font-serif border border-gray-200 p-4 sm:p-5 md:p-6 shadow rounded-lg mb-4"
        itemScope 
        itemType="https://schema.org/DefinedTerm"
    >
        <header className="mb-3 sm:mb-4">
            <h1 className="text-3xl sm:text-4xl font-bold" itemProp="name">
                <dfn>vizbadger</dfn>
            </h1>
            <p className="text-lg sm:text-xl mt-1.5">
                {/* Pronunciation note: /vɪz/ˈbadʒə/ - Could also be /ˈvɪzbædʒər/ or /vɪzˈbædʒər/ 
                    Current uses /a/ which may be British English variant. 
                    Consider /æ/ (ash) for more standard IPA: /ˈvɪzbædʒər/ */}
                <span className="text-gray-500 italic" aria-label="Pronunciation">
                    /vɪz/ˈbadʒə/
                </span>
                <span className="mx-1" aria-hidden="true">·</span>
                <span className="italic" itemProp="additionalType">noun</span>
            </p>
            <p className="text-xs sm:text-sm text-gray-600 italic mt-1.5 mb-3 sm:mb-4" itemProp="description">
                <strong>Origin:</strong> Early 21st century; blend of 'visualization' and 'badger', referring to one who persistently advocates for reliable analytics engineering to enable better data-driven decision making.
            </p>
        </header>

        <dl className="my-3 sm:my-4">
            <dt className="sr-only">Similar terms</dt>
            <dd>
                {/* Note on styling: The green color (text-badger-green) works well as it adds visual interest 
                    and matches your brand colors. However, consider if it should match the dictionary's 
                    more muted aesthetic - perhaps text-gray-700 with font-medium would be more traditional.
                    The current green is playful and stands out, which fits the lighthearted parody tone. */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <span className="text-badger-green mr-1 sm:mr-2 font-medium text-sm sm:text-base leading-none">Similar:</span>
                    {similarTerms.map((term, index) => (
                        <span 
                            key={index}
                            className="border text-gray-600 px-2 py-1 text-xs sm:text-sm rounded-full leading-tight"
                            itemProp="relatedTo"
                        >
                            {term}
                        </span>
                    ))}
                </div>
            </dd>
        </dl>

        <div className="my-3 sm:my-4 py-2 sm:py-3" itemProp="description">
            <span className="font-medium">1.</span> A person specializing in analytics engineering, data infrastructure design, and communicating data effectively.
            Their expertise spans designing analytics schemas, building data pipelines, predictive modeling,
            and creating impactful visualizations—whether for enterprise analytics systems or sports analytics projects.
        </div>

        <section className="mt-4 sm:mt-5 pt-3 sm:pt-4 border-t border-gray-200">
            <h2 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3 sr-only">Usage examples</h2>
            <blockquote className="space-y-2 sm:space-y-3">
                <p className="text-gray-500 italic text-xs sm:text-sm pl-2 sm:pl-3" itemProp="exampleOfWork">
                    "They won't stop insisting we maintain granularity at the source. He is such a vizbadger."
                </p>
                <p className="text-gray-500 italic text-xs sm:text-sm pl-2 sm:pl-3" itemProp="exampleOfWork">
                    "This vizbadger just ranted for 5 minutes about how 7/1 for Jon Rahm is not a good value bet."
                </p>
                <p className="text-gray-500 italic text-xs sm:text-sm pl-2 sm:pl-3" itemProp="exampleOfWork">
                    "They won't stop suggesting we plot the data. He is such a
                    vizbadger."
                </p>
            </blockquote>
        </section>

        <footer className="mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-gray-200 text-center font-sans">
            <p className="text-xs sm:text-sm text-gray-600 mb-3">Connect:</p>
            <nav aria-label="Social media links" className="flex items-center justify-center gap-3 sm:gap-4">
                <a 
                    href="https://www.linkedin.com/in/william-canniford/" 
                    className="text-badger-blue hover:text-badger-green transition-colors text-xs sm:text-sm flex items-center gap-1"
                    target="_blank" 
                    rel="noreferrer noopener"
                    aria-label="LinkedIn profile (opens in new tab)"
                >
                    <svg
                        className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 448 512"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z" />
                    </svg>
                    <span className="leading-none">LinkedIn</span>
                    <svg 
                        className="w-2.5 h-2.5 sm:w-3 sm:h-3 opacity-60 flex-shrink-0" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        style={{ marginTop: '0.125rem' }}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                </a>
                <a 
                    href="https://github.com/willcanniford" 
                    className="text-badger-blue hover:text-badger-green transition-colors text-xs sm:text-sm flex items-center gap-1"
                    target="_blank" 
                    rel="noreferrer noopener"
                    aria-label="GitHub profile (opens in new tab)"
                >
                    <svg
                        className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 496 512"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3.3-5.6-1.3-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.3 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5.3-6.2 2.3zm44.2-1.7c-2.9.7-4.9 2.6-4.6 4.9.3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8zM97.2 352.9c-1.3 1-1 3.3.7 5.2 1.6 1.6 3.9 2.3 5.2 1 1.3-1 1-3.3-.7-5.2-1.6-1.6-3.9-2.3-5.2-1zm-10.8-8.1c-.7 1.3.3 2.9 2.3 3.9 1.6 1 3.6.7 4.3-.7.7-1.3-.3-2.9-2.3-3.9-2-.6-3.6-.3-4.3.7zm32.4 35.6c-1.6 1.3-1 4.3 1.3 6.2 2.3 2.3 5.2 2.6 6.5 1 1.3-1.3.7-4.3-1.3-6.2-2.2-2.3-5.2-2.6-6.5-1zm-11.4-14.7c-1.6 1-1.6 3.6 0 5.9 1.6 2.3 4.3 3.3 5.6 2.3 1.6-1.3 1.6-3.9 0-6.2-1.4-2.3-4-3.3-5.6-2z" />
                    </svg>
                    <span className="leading-none">GitHub</span>
                    <svg 
                        className="w-2.5 h-2.5 sm:w-3 sm:h-3 opacity-60 flex-shrink-0" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        style={{ marginTop: '0.125rem' }}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                </a>
            </nav>
        </footer>
    </article>
);
